import {
  listenFeaturedBusinesses,
  type FeaturedBusinessRow,
} from "@/lib/directory/featuredBusinesses";
import { router } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import {
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

type Props = {
  excludeBusinessId?: string | null;
};

export default function FeaturedVertical({
  excludeBusinessId = null,
}: Props) {
  const [items, setItems] = useState<FeaturedBusinessRow[]>([]);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const unsub = listenFeaturedBusinesses(
      { limitCount: 22, excludeBusinessId },
      (rows) => {
        setItems(rows);
        setHasError(false);
      },
      (error) => {
        console.log("FAILED: FeaturedVertical businesses listener", error);
        setItems([]);
        setHasError(true);
      }
    );

    return () => unsub();
  }, [excludeBusinessId]);

  const data = useMemo(() => items.filter((item) => !!item.slug), [items]);

  if (hasError || !data.length) return null;

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Featured Businesses</Text>

      <View style={styles.scrollFrame}>
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator
          nestedScrollEnabled
          bounces={false}
        >
          {data.map((item) => (
            <Pressable
              key={item.id}
              onPress={() => {
                if (!item.slug) return;
                router.push({
                  pathname: "/p/[slug]",
                  params: { slug: item.slug },
                });
              }}
              style={styles.card}
            >
              <Image
                source={{
                  uri:
                    item.logoUrl ||
                    "https://via.placeholder.com/120x120.png?text=LOGO",
                }}
                style={styles.logo}
              />

              <View style={styles.body}>
                <Text style={styles.name} numberOfLines={1}>
                  {item.name || "Business"}
                </Text>

                <Text style={styles.meta} numberOfLines={2}>
                  {[item.category, item.cityLine].filter(Boolean).join(" • ")}
                </Text>
              </View>
            </Pressable>
          ))}
        </ScrollView>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 0,
  },

  title: {
    fontSize: 20,
    fontWeight: "900",
    color: "#111827",
    marginBottom: 12,
    flexShrink: 0,
  },

  scrollFrame: {
    flex: 1,
    minHeight: 0,
    maxHeight: "100%",
  },

  scroll: {
    flex: 1,
    minHeight: 0,
  },

  scrollContent: {
    paddingTop: 2,
    paddingBottom: 80,
    paddingRight: 8,
  },

  card: {
    flexDirection: "row",
    gap: 10,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 16,
    padding: 10,
    backgroundColor: "#fff",
    alignItems: "center",
    marginBottom: 12,
  },

  logo: {
    width: 56,
    height: 56,
    borderRadius: 12,
    backgroundColor: "#e5e7eb",
    flexShrink: 0,
  },

  body: {
    flex: 1,
    minWidth: 0,
  },

  name: {
    fontSize: 15,
    fontWeight: "900",
    color: "#111827",
  },

  meta: {
    fontSize: 12,
    color: "#6b7280",
    marginTop: 4,
    lineHeight: 18,
  },
});